import { Logger } from '../../../packages/logger/src';
import { whenDeviceRegistered } from '../../../packages/widgets/src/services/WebexInstanceService';
import { CALLING_BACKEND, getCallingBackEnd } from '../../../packages/widgets/src/utils/callingUtils';

/**
 * Per-backend developer feature toggles for the multi-line "Call As" picker.
 *
 * - WXC uses the original (already GA'd) toggle.
 * - UCM uses a separate toggle so its rollout can be staged independently
 *   without re-gating the GA'd WXC experience.
 *
 * Ref: UCM_LINE_SELECTION_DESIGN.md §6
 */
const FEATURE_TOGGLE_WXC = 'desktop-msteams-multiline-selection';
const FEATURE_TOGGLE_UCM = 'spark-812658-desktop-msteams-ucm-multiline-selection';

/**
 * Check if line selection feature is enabled via the per-backend feature toggle.
 *
 * - WXC users  → 'desktop-msteams-multiline-selection' (GA)
 * - UCM users  → 'spark-812658-desktop-msteams-ucm-multiline-selection' (rollout)
 * - Other backends (BWRKS, INVALID) → not supported, returns false
 *
 * In MS Teams embedded browser, device registration may be delayed.
 * This function adds timeout protection to avoid hanging indefinitely.
 */
export async function isLineSelectionFeatureEnabled(): Promise<boolean> {
  try {
    // Wait for device registration with timeout (30s max)
    const deviceReadyPromise = whenDeviceRegistered();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Device registration timeout after 30s')), 30000)
    );

    await Promise.race([deviceReadyPromise, timeoutPromise]);
    Logger.debug('[LineSelection] Device registered — resolving toggle for backend');

    // Pick the correct toggle for the user's calling backend.
    const backend = getCallingBackEnd();
    let toggleName: string | null = null;
    if (backend === CALLING_BACKEND.WXC) {
      toggleName = FEATURE_TOGGLE_WXC;
    } else if (backend === CALLING_BACKEND.UCM) {
      toggleName = FEATURE_TOGGLE_UCM;
    } else {
      Logger.info(`[LineSelection] Backend "${backend}" not supported for multi-line — feature disabled`);
      return false;
    }

    // Check feature toggle with timeout (5s max)
    const featurePromise = window.webex.internal.feature.getFeature('developer', toggleName);
    const featureTimeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Feature toggle check timeout after 5s')), 5000)
    );

    const result = await Promise.race([featurePromise, featureTimeoutPromise]);
    const enabled = !!result;
    Logger.info(`[LineSelection] Feature toggle "${toggleName}" (${backend}) — enabled: ${enabled}`);
    return enabled;
  } catch (error) {
    Logger.info(`[LineSelection] Feature toggle check failed (may be normal in MS Teams embedded browser): ${error}`);
    // Default to disabled if check fails, but this is expected behavior
    return false;
  }
}
